'use client';

import { Box, Container, ContainerProps, Grid, List, ListItem, Stack } from '@mui/material';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import Image from 'next/image';
import { ReactNode, useMemo, useState } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { EmailInputCTA, NumberInputCTA, PlainButtonCTA } from '@/components/Hero';
import MarqueeBullets from '@/components/MarqueeBullets';
import PhoneImageGallery from '@/components/PhoneImageGallery';
import { StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import {
  ComponentBlocksButton,
  ComponentBlocksLandingPageHero,
  Enum_Componentblockslandingpagehero_Landingheromediadisplay,
  UploadFile,
} from '@/types';

export interface LandingPageHeroProps {
  block: ComponentBlocksLandingPageHero | undefined;
  manualContent?: ReactNode;
  containerProps?: ContainerProps;
  noCta?: boolean;
}

export default function LandingPageHero({
  block,
  containerProps = {},
  manualContent = undefined,
  noCta = false,
}: LandingPageHeroProps) {
  const [rawAmount, setRawAmount] = useState('50,000');
  const [videoError, setVideoError] = useState(false);

  const isHeroDisplay =
    block?.LandingHeroMediaDisplay ===
    Enum_Componentblockslandingpagehero_Landingheromediadisplay.Hero;

  const bullets = useMemo(() => {
    return block?.LandingHeroBullets ? block?.LandingHeroBullets?.split('\n') : [];
  }, [block?.LandingHeroBullets]);

  const visibleCTAs = useMemo(
    () =>
      noCta
        ? []
        : block?.LandingHeroCTA?.filter((c): c is ComponentBlocksButton =>
            Boolean(c?.ShowButton)
          ) || [],
    [block?.LandingHeroCTA, noCta]
  );

  const landingHeroMediaUrl = getImageUrl({
    image: block?.LandingHeroMedia,
    size: 'url',
  });
  const mediaElement = useMemo(() => {
    if (getImageUrl({ image: block?.LandingHeroMobileMedia?.[0], size: 'url' })) {
      return (
        <PhoneImageGallery
          images={(block?.LandingHeroMobileMedia ?? []) as UploadFile[]}
          alt={'Images of the LendIQ App'}
        />
      );
    }
    if (!landingHeroMediaUrl) return null;

    const mediaBoxStyles = isHeroDisplay
      ? {
          position: 'relative' as const,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          borderRadius: '1.5rem',
        }
      : {
          position: 'relative' as const,
          width: '100%',
          height: { xs: '300px', sm: '400px', md: '500px' },
          borderRadius: '1.5rem',
          paddingLeft: { md: '2rem' },
          overflow: 'hidden',
        };

    const mediaStyles = {
      width: '100%',
      height: '100%',
      objectFit: isHeroDisplay ? ('cover' as const) : (block?.LandingHeroMediaDisplay as any),
      objectPosition: 'top',
      borderRadius: '1.5rem',
    };

    // Check if it's a video
    if (block?.LandingHeroMedia?.mime?.startsWith('video/') && landingHeroMediaUrl && !videoError) {
      return (
        <Box sx={mediaBoxStyles}>
          <video
            autoPlay
            muted
            loop
            playsInline
            style={mediaStyles}
            onError={() => setVideoError(true)}
          >
            <source src={landingHeroMediaUrl} type={block?.LandingHeroMedia.mime} />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    // Default to image
    return (
      <Box sx={mediaBoxStyles}>
        <Image
          src={landingHeroMediaUrl}
          alt={block?.LandingHeroMedia?.alternativeText || 'Landing page hero media'}
          width={block?.LandingHeroMedia?.width ?? undefined}
          height={block?.LandingHeroMedia?.height ?? undefined}
          priority
          fetchPriority="high"
          style={mediaStyles}
          unoptimized
        />
      </Box>
    );
  }, [
    block?.LandingHeroMedia,
    block?.LandingHeroMediaDisplay,
    landingHeroMediaUrl,
    block?.LandingHeroMobileMedia,
    videoError,
    isHeroDisplay,
  ]);

  const contentBlock = (
    <Box
      className={`landing-hero-content ${mediaElement === null ? '' : 'with-media'}`}
      mb={{ xs: 3, md: 8 }}
    >
      {/* Hero Blurb */}
      {block?.LandingHeroBlurb && (
        <Box sx={{ mb: 3 }}>
          <GlobalBlockRenderer content={block?.LandingHeroBlurb} />
        </Box>
      )}

      {/* Hero Bullets (non-marquee mode stays above CTA) */}
      {bullets && bullets.length > 0 && !block?.LandingHeroMarqueeBullets && (
        <List
          sx={(theme) => ({
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-start',
            columnGap: theme.spacing(1),
            rowGap: theme.spacing(1),
            mb: theme.spacing(2),
            pr: theme.spacing(6),
          })}
        >
          {bullets.map((value) => (
            <ListItem
              key={value}
              sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: theme.spacing(1.5),
                width: 'auto',
                p: 0,
                mr: theme.spacing(4),
                svg: {
                  color: theme.palette.brand.green,
                },
              })}
            >
              <IconCircleCheckFilled />
              {value}
            </ListItem>
          ))}
        </List>
      )}

      {manualContent}

      {/* CTAs */}
      {!noCta && (
        <Stack
          direction="column"
          spacing={2}
          mb={3}
          mt={5}
          maxWidth={{
            xs: '100%',
            md: '25rem',
          }}
        >
          {visibleCTAs.length > 0 ? (
            visibleCTAs.map((cta, index) => {
              if (cta.ButtonNumberValue) {
                return (
                  <NumberInputCTA
                    key={cta.id || index}
                    cta={cta}
                    rawAmount={rawAmount}
                    setRawAmount={setRawAmount}
                  />
                );
              } else if (cta.ButtonPlaceholder) {
                return <EmailInputCTA key={cta.id || index} cta={cta} />;
              } else {
                return <PlainButtonCTA key={cta.id || index} cta={cta} />;
              }
            })
          ) : (
            <NumberInputCTA
              cta={
                {
                  ButtonText: 'Get started',
                  ButtonPlaceholder: '50,000.00',
                } as ComponentBlocksButton
              }
              rawAmount={rawAmount}
              setRawAmount={setRawAmount}
            />
          )}
        </Stack>
      )}

      {/* Hero Bullets (marquee mode moves below CTA) */}
      {bullets && bullets.length > 0 && block?.LandingHeroMarqueeBullets && (
        <MarqueeBullets items={bullets} />
      )}
    </Box>
  );

  return (
    <CustomBackground
      backgroundOptions={{
        BackgroundColor: block?.LandingHeroBackground?.BackgroundColor ?? 'purple-800',
        BackgroundOrbs: block?.LandingHeroBackground?.BackgroundOrbs ?? 'none',
        ContainedBackground: block?.LandingHeroBackground?.ContainedBackground ?? false,
      }}
      displayOptions={block?.LandingHeroDisplayOptions as StrapiDisplayOptions}
      boxProps={{
        id: 'landing-hero',
        sx: {
          pt: { xs: 4, sm: 3, md: 4, lg: 6 },
          pb: { xs: 6, sm: 8, md: 10 },
        },
      }}
    >
      {isHeroDisplay ? (
        <Container
          {...containerProps}
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: { xs: `calc(100vh - 20rem)`, lg: `calc(100vh - 20rem)` },
            ...(containerProps?.sx ?? {}),
          }}
        >
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={6}>
              {contentBlock}
            </Grid>
            {mediaElement && (
              <Grid item xs={12} md={6}>
                {mediaElement}
              </Grid>
            )}
          </Grid>
        </Container>
      ) : (
        <Container
          {...containerProps}
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: { xs: 'space-between', sm: 'center' },
            rowGap: 3,
            columnGap: 1,
            p: 0,
            '.landing-hero-content': {
              width: {
                xs: '100%',
                sm: '44%',
                md: '55%',
              },
            },
            '.landing-hero-media': {
              width: 'auto',
              maxWidth: { xs: '100%', sm: '40%', md: '35%' },
              marginInline: {
                xs: 'auto',
                sm: 0,
              },
            },
            ...(containerProps?.sx ?? {}),
          }}
        >
          {contentBlock}
          {mediaElement && <Box className="landing-hero-media">{mediaElement}</Box>}
        </Container>
      )}
    </CustomBackground>
  );
}
